import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalSwipeViews } from '../hooks/useSwipeViews';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { MyPedalsChromeProvider, useMyPedalsChrome } from '../contexts/MyPedalsChromeContext';
import MyPedalsMobileChromeHeader from '../components/MyPedalsMobileChromeHeader';
import Pedalboard from './Pedalboard';
import WantList from './WantList';
import OnboardingTip from '../components/OnboardingTip';
import useOnboardingTips from '../hooks/useOnboardingTips';

const MyPedalsHubContent = () => {
  // First-visit tip (Jason, 2026-10-20): point at the My Wants tab.
  const { activeTip: hubTip, dismiss: dismissHubTip, delay: hubTipDelay } = useOnboardingTips([{ id: 'myPedalsWants' }]);
  const location = useLocation();
  const navigate = useNavigate();
  const { rightSlot, addSlot } = useMyPedalsChrome();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const isWantsTab = location.pathname.includes('/wants');
  const activeTab = isWantsTab ? 'wants' : 'pedalboard';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // The actual tab switch (route + scroll reset). The hub stays mounted across
  // the route change, so the window keeps its scroll offset and would drop the
  // user mid-page on the other tab; reset to the top so each tab starts at top.
  // Via scrollPageToTop because the incoming tab's layout is not committed yet
  // and, on iOS, the WebView restores its own offset over a plain scrollTo.
  const scrollCancelRef = useRef(null);
  const goToIndex = (next) => {
    navigate(next === 1 ? '/my-pedals/wants' : '/my-pedals/pedalboard');
    scrollCancelRef.current?.();
    scrollCancelRef.current = scrollPageToTop();
  };
  useEffect(() => () => scrollCancelRef.current?.(), []);

  // Native-only swipe + slide between the two tabs (Pedalboard <-> Wantlist).
  // liveDrag makes the pane track the thumb during the gesture, and reports the
  // position the toggle's indicator should follow, so the swipe is discoverable
  // in the first place rather than only paying off once it completes.
  const tabIndex = activeTab === 'wants' ? 1 : 0;
  // Pedalboard's inline edit form reorders photos with a horizontal drag; the
  // tab-swap swipe was stealing that drag and yanking the user out of the edit
  // (Pixel 4a, 2026-08-21). The form reports gesture ownership; swap disabled
  // while it does — the toggle buttons still switch tabs.
  const [formOwnsGesture, setFormOwnsGesture] = useState(false);
  const { swipeProps, viewClass, transitionTo } =
    useLocalSwipeViews(tabIndex, 2, goToIndex, { liveDrag: true, enabled: !formOwnsGesture });

  // Toggle taps run through the same animated transition (instant on web).
  const handleTabChange = (tab) => transitionTo(tab === 'wants' ? 1 : 0);

  // The gesture itself no longer re-renders this component, but a tab change
  // still does; memoising keeps the Firestore-backed subtree from being rebuilt
  // for the header's own state changes (the scrolled-down compact bar).
  const activeView = useMemo(
    () => (activeTab === 'pedalboard' ? <Pedalboard onOwnsHorizontalGesture={setFormOwnsGesture} /> : <WantList />),
    [activeTab]
  );

  return (
    // minHeight was 'auto' (overriding the min-h-screen class), so with no
    // pedals the cream surface ended mid-viewport and the body's fixed
    // white-over-beige gradient showed through as a bare white block above
    // the tab bar (found during Android testing, 2026-08-20).
    <div className="min-h-screen" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      {hubTip && <OnboardingTip key={hubTip} tipId={hubTip} onGotIt={dismissHubTip} delay={hubTipDelay} />}
      <style jsx>{`
        body {
          background:
            linear-gradient(to bottom,
              #ffffff 0%,
              #ffffff calc(100% - 150px),
              #F7F3EB calc(100% - 150px),
              #F7F3EB 100%) fixed !important;
        }
      `}</style>

      <div
        {...swipeProps}
        className="my-pedals-page-content"
        style={{ paddingTop: '2rem', paddingBottom: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <MyPedalsMobileChromeHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          rightSlot={rightSlot}
        >
          <div
            className="my-pedals-hub-header-row"
            style={{
              maxWidth: '1100px',
              margin: '0 auto 1.5rem auto',
              padding: '0 2rem',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div
                style={{
                  display: 'inline-flex',
                  // Raised selected pill, inset track (Jason, 2026-10-20); the
                  // track keeps its original white.
                  backgroundColor: 'white',
                  borderRadius: isMobile ? '25px' : '30px',
                  padding: isMobile ? '4px' : '6px',
                  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.06)',
                  border: isMobile ? '1px solid #e5e7eb' : '1px solid #d1d5db',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleTabChange('pedalboard')}
                  style={{
                    padding: isMobile ? '0.5rem 1.5rem' : '0.75rem 2.5rem',
                    borderRadius: isMobile ? '25px' : '30px',
                    border: 'none',
                    fontWeight: isMobile ? '600' : '700',
                    fontSize: isMobile ? '1rem' : '1.125rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: activeTab === 'pedalboard' ? '#C89F3C' : 'transparent',
                    color: activeTab === 'pedalboard' ? 'white' : '#6B7280',
                    boxShadow: activeTab === 'pedalboard' ? '0 2px 6px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.35)' : 'none',
                  }}
                >
                  My Pedalboard
                </button>
                <button
                  type="button"
                  data-tip-anchor="my-wants-tab"
                  onClick={() => handleTabChange('wants')}
                  style={{
                    padding: isMobile ? '0.5rem 1.5rem' : '0.75rem 2.5rem',
                    borderRadius: isMobile ? '25px' : '30px',
                    border: 'none',
                    fontWeight: isMobile ? '600' : '700',
                    fontSize: isMobile ? '1rem' : '1.125rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: activeTab === 'wants' ? '#C89F3C' : 'transparent',
                    color: activeTab === 'wants' ? 'white' : '#6B7280',
                    boxShadow: activeTab === 'wants' ? '0 2px 6px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.35)' : 'none',
                  }}
                >
                  My Wants
                </button>
              </div>
            </div>
            {addSlot ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                {addSlot}
              </div>
            ) : null}
          </div>
        </MyPedalsMobileChromeHeader>

        <div className="swipe-clip" style={{ flex: 1, backgroundColor: '#F7F3EB' }}>
          <div key={activeTab} className={['swipe-pane', viewClass].filter(Boolean).join(' ')}>
            {activeView}
          </div>
        </div>
      </div>
    </div>
  );
};

const MyPedalsHub = () => (
  <MyPedalsChromeProvider>
    <MyPedalsHubContent />
  </MyPedalsChromeProvider>
);

export default MyPedalsHub;
